// Integrations (PMU-188, PMU-189): the organisation's API keys for the public API (/api/public/v1)
// and its webhook endpoints. Only organisation administrators with a second factor. A key's or an
// endpoint's secret is shown once; sensitive scopes (restrictions) are off unless ticked.
import { useState } from "react";
import { Badge, Banner, Button, Checkbox, Dialog, DialogClose, Sheet, Table, TextField } from "@vavr/ui";
import { api, dateTime } from "../shared/index.ts";
import { useConsole, useGet, useAction } from "./data.tsx";
import { ConfirmButton, LoadError, Loading, Toolbar } from "./common.tsx";

interface ScopeInfo { id: string; label: string; sensitive: boolean }
interface Key { id: string; name: string; scopes: string[]; site_ids: string[] | null; created_at: number; last_used_at: number | null; revoked_at: number | null; created_by: string | null }

export function Integrations() {
  const { site } = useConsole();
  const path = `/api/v1/orgs/${site.org_id}/api-keys`;
  const keys = useGet<{ keys: Key[]; scopes: ScopeInfo[] }>(path);
  const act = useAction();
  if (keys.loading) return <Loading />;
  if (keys.error) return <LoadError error={keys.error} retry={keys.reload} />;
  const scopes = keys.data?.scopes ?? [];
  return (
    <div className="pmu-stack">
      <h2 className="pmu-c-h3">API keys</h2>
      <p className="pmu-muted">API keys let your own systems, such as a student information system, read from Vavr. Each key sees only what its scopes allow, and every read is recorded in the audit log. Details are in the API reference at <code>/api/public/v1/openapi.json</code>.</p>
      <Toolbar actions={<AddKey path={path} scopes={scopes} />} />
      {act.error && <Banner tone="danger" title={act.error} />}
      <Table
        caption="API keys" hideCaption rowKey={(k) => k.id} rows={keys.data?.keys ?? []} empty="No API keys yet."
        columns={[
          { key: "name", header: "Key", rowHeader: true, render: (k) => <span className="pmu-stack pmu-c-tight"><span>{k.name}</span><span className="pmu-small pmu-muted">{k.created_by ? `Created by ${k.created_by}, ` : ""}{dateTime(k.created_at, site.tz)}</span></span> },
          { key: "scopes", header: "Can see", render: (k) => <span className="pmu-row">{k.scopes.map((s) => <Badge key={s} tone={scopes.find((x) => x.id === s)?.sensitive ? "warning" : "neutral"}>{s}</Badge>)}</span> },
          { key: "used", header: "Last used", render: (k) => (k.last_used_at ? dateTime(k.last_used_at, site.tz) : "Never") },
          { key: "status", header: "Status", render: (k) => (k.revoked_at ? <Badge>Revoked</Badge> : <Badge tone="success">Active</Badge>) },
          {
            key: "act", header: <span className="pmu-sr-only">Actions</span>, align: "end", render: (k) => k.revoked_at ? null : (
              <ConfirmButton size="sm" variant="ghost" danger title={`Revoke ${k.name}?`} description="Anything using this key stops working at once. This cannot be undone; create a new key to reconnect." confirmLabel="Revoke"
                onConfirm={() => act.run(() => api("DELETE", `${path}/${k.id}`), { success: `${k.name} revoked.`, invalidate: [path] })}>Revoke</ConfirmButton>
            ),
          },
        ]}
      />
      <Webhooks />
    </div>
  );
}

/** A secret shown once, with a copy button (the clipboard may refuse; the text stays selectable). */
function SecretOnce({ secret, what, advice }: { secret: string; what: string; advice: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="pmu-stack">
      <Banner tone="warning" title={`This is the only time the ${what} is shown`}>{advice}</Banner>
      <p className="pmu-c-code pmu-c-code--wrap">{secret}</p>
      <div><Button variant="secondary" icon="copy" onClick={() => navigator.clipboard?.writeText(secret).then(() => setCopied(true), () => undefined)}>{copied ? "Copied" : `Copy the ${what}`}</Button></div>
    </div>
  );
}

function AddKey({ path, scopes }: { path: string; scopes: ScopeInfo[] }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [created, setCreated] = useState<string | null>(null);
  const act = useAction();
  const reset = () => { setName(""); setChosen(new Set(scopes.filter((s) => !s.sensitive).map((s) => s.id))); setCreated(null); };
  const create = async () => {
    const r = await act.run(() => api<{ key: string }>("POST", path, { name, scopes: [...chosen] }), { invalidate: [path] });
    if (r) setCreated(r.key);
  };
  return (
    <Dialog title={created ? "Copy your key now" : "Create an API key"} open={open} onOpenChange={(o) => { setOpen(o); if (o) reset(); }}
      trigger={<Button icon="plus">Create an API key</Button>}
      actions={created ? <DialogClose><Button>Done</Button></DialogClose> : <><DialogClose><Button variant="secondary">Cancel</Button></DialogClose><Button loading={act.busy} disabled={!name.trim() || !chosen.size} onClick={create}>Create key</Button></>}>
      {created ? (
        <SecretOnce secret={created} what="key" advice="Store it in your system's secret settings. If you lose it, revoke it and create another." />
      ) : (
        <form className="pmu-stack" onSubmit={(e) => { e.preventDefault(); if (name.trim() && chosen.size) void create(); }}>
          {act.error && <Banner tone="danger" title={act.error} />}
          <TextField label="Name" required placeholder="Student information system" value={name} onChange={(e) => setName(e.target.value)} />
          <fieldset className="pmu-stack">
            <legend className="pmu-c-h3">What it can see</legend>
            {scopes.map((s) => (
              <Checkbox key={s.id} label={s.label} description={s.sensitive ? `${s.id} · sensitive: only if this system must know` : s.id} checked={chosen.has(s.id)}
                onCheckedChange={(on) => { const n = new Set(chosen); if (on) n.add(s.id); else n.delete(s.id); setChosen(n); }} />
            ))}
          </fieldset>
          <button type="submit" hidden />
        </form>
      )}
    </Dialog>
  );
}

// ---------- webhooks (PMU-189) ----------

interface Endpoint { id: string; url: string; description: string | null; events: string[]; created_at: number; disabled_at: number | null; failed_7d: number; last_delivered_at: number | null }
interface Delivery { id: string; event_type: string; state: "pending" | "delivered" | "failed"; attempts: number; last_status: number | null; last_error: string | null; last_attempt_at: number | null; next_attempt_at: number | null; created_at: number }

function Webhooks() {
  const { site } = useConsole();
  const path = `/api/v1/orgs/${site.org_id}/webhooks`;
  const hooks = useGet<{ endpoints: Endpoint[]; events: { id: string; label: string }[] }>(path);
  const [log, setLog] = useState<Endpoint | null>(null);
  const act = useAction();
  if (hooks.loading) return <Loading />;
  if (hooks.error) return <LoadError error={hooks.error} retry={hooks.reload} />;
  return (
    <section className="pmu-stack" aria-labelledby="pmu-webhooks">
      <h2 id="pmu-webhooks" className="pmu-c-h3">Webhooks</h2>
      <p className="pmu-muted">Vavr tells your system as things happen: a POST to your address, signed with <code>Vavr-Signature</code> (HMAC-SHA256 of <code>timestamp.body</code> with the endpoint's secret). Failed deliveries are retried for about 11 hours.</p>
      <Toolbar actions={<AddEndpoint path={path} events={hooks.data?.events ?? []} />} />
      {act.error && <Banner tone="danger" title={act.error} />}
      <Table
        caption="Webhook endpoints" hideCaption rowKey={(e) => e.id} rows={hooks.data?.endpoints ?? []} empty="No webhook endpoints yet."
        columns={[
          { key: "url", header: "Endpoint", rowHeader: true, render: (e) => <span className="pmu-stack pmu-c-tight"><span className="pmu-c-break">{e.url}</span>{e.description && <span className="pmu-small pmu-muted">{e.description}</span>}</span> },
          { key: "events", header: "Events", render: (e) => <span className="pmu-row">{e.events.map((x) => <Badge key={x}>{x}</Badge>)}</span> },
          { key: "health", header: "Deliveries", render: (e) => e.disabled_at ? <Badge>Disabled</Badge> : e.failed_7d ? <Badge tone="warning">{e.failed_7d} failed this week</Badge> : e.last_delivered_at ? `Last ${dateTime(e.last_delivered_at, site.tz)}` : "None yet" },
          {
            key: "act", header: <span className="pmu-sr-only">Actions</span>, align: "end", render: (e) => (
              <span className="pmu-row">
                <Button size="sm" variant="ghost" onClick={() => setLog(e)}>Deliveries</Button>
                {!e.disabled_at && <Button size="sm" variant="ghost" disabled={act.busy} onClick={() => act.run(() => api("POST", `${path}/${e.id}/test`), { success: "Test sent. It shows in Deliveries.", invalidate: [path] })}>Send a test</Button>}
                {!e.disabled_at && (
                  <ConfirmButton size="sm" variant="ghost" danger title="Disable this endpoint?" description="Vavr stops sending to it at once, including retries. Add it again to start over with a new secret." confirmLabel="Disable"
                    onConfirm={() => act.run(() => api("DELETE", `${path}/${e.id}`), { success: "Endpoint disabled.", invalidate: [path] })}>Disable</ConfirmButton>
                )}
              </span>
            ),
          },
        ]}
      />
      <Sheet title="Deliveries" description={log?.url} open={log !== null} onOpenChange={(o) => !o && setLog(null)}>
        {log && <DeliveryLog path={`${path}/${log.id}`} />}
      </Sheet>
    </section>
  );
}

function DeliveryLog({ path }: { path: string }) {
  const { site } = useConsole();
  const d = useGet<{ deliveries: Delivery[] }>(`${path}/deliveries`);
  const act = useAction();
  if (d.loading) return <Loading />;
  if (d.error) return <LoadError error={d.error} retry={d.reload} />;
  const tone = { delivered: "success", pending: "info", failed: "danger" } as const;
  return (
    <div className="pmu-stack">
      {act.error && <Banner tone="danger" title={act.error} />}
      <Table
        caption="Deliveries, newest first (kept 30 days)" rowKey={(x) => x.id} rows={d.data?.deliveries ?? []} empty="Nothing sent yet."
        columns={[
          { key: "what", header: "Event", rowHeader: true, render: (x) => <span className="pmu-stack pmu-c-tight"><span>{x.event_type}</span><span className="pmu-small pmu-muted">{dateTime(x.created_at, site.tz)}</span></span> },
          { key: "state", header: "Result", render: (x) => <span className="pmu-stack pmu-c-tight"><Badge tone={tone[x.state]}>{x.state === "pending" && x.attempts ? "Retrying" : x.state}</Badge><span className="pmu-small pmu-muted">{x.last_error ?? (x.last_status ? `HTTP ${x.last_status}` : "")}{x.attempts > 1 ? ` · ${x.attempts} attempts` : ""}</span></span> },
          { key: "act", header: <span className="pmu-sr-only">Actions</span>, align: "end", render: (x) => x.state !== "failed" ? null : (
            <Button size="sm" variant="ghost" disabled={act.busy} onClick={() => act.run(() => api("POST", `${path}/deliveries/${x.id}/retry`), { success: "Sent again.", invalidate: [`${path}/deliveries`] })}>Resend</Button>
          ) },
        ]}
      />
    </div>
  );
}

function AddEndpoint({ path, events }: { path: string; events: { id: string; label: string }[] }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [secret, setSecret] = useState<string | null>(null);
  const act = useAction();
  const reset = () => { setUrl(""); setDescription(""); setChosen(new Set(events.map((e) => e.id))); setSecret(null); };
  const create = async () => {
    const r = await act.run(() => api<{ secret: string }>("POST", path, { url, description: description || null, events: [...chosen] }), { invalidate: [path] });
    if (r) setSecret(r.secret);
  };
  const ready = url.trim().startsWith("https://") && chosen.size > 0;
  return (
    <Dialog title={secret ? "Copy the signing secret now" : "Add a webhook endpoint"} open={open} onOpenChange={(o) => { setOpen(o); if (o) reset(); }}
      trigger={<Button icon="plus" variant="secondary">Add an endpoint</Button>}
      actions={secret ? <DialogClose><Button>Done</Button></DialogClose> : <><DialogClose><Button variant="secondary">Cancel</Button></DialogClose><Button loading={act.busy} disabled={!ready} onClick={create}>Add endpoint</Button></>}>
      {secret ? (
        <SecretOnce secret={secret} what="secret" advice="Your system checks each delivery's Vavr-Signature with it. If you lose it, disable the endpoint and add it again." />
      ) : (
        <form className="pmu-stack" onSubmit={(e) => { e.preventDefault(); if (ready) void create(); }}>
          {act.error && <Banner tone="danger" title={act.error} />}
          <TextField label="Address" required type="url" placeholder="https://sis.example.org/vavr" hint="https only, on a public host name." value={url} onChange={(e) => setUrl(e.target.value)} />
          <TextField label="Description" placeholder="Student information system" value={description} onChange={(e) => setDescription(e.target.value)} />
          <fieldset className="pmu-stack">
            <legend className="pmu-c-h3">Events</legend>
            {events.map((ev) => (
              <Checkbox key={ev.id} label={ev.label} description={ev.id} checked={chosen.has(ev.id)}
                onCheckedChange={(on) => { const n = new Set(chosen); if (on) n.add(ev.id); else n.delete(ev.id); setChosen(n); }} />
            ))}
          </fieldset>
          <button type="submit" hidden />
        </form>
      )}
    </Dialog>
  );
}
